import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Headphones,
  Server,
  Shield,
  Settings,
  Database,
  Users,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { IT_SUPPORT_DATA, CASE_STUDIES } from "../data/mock";
import {
  IndustriesSection,
  EngagementModelsSection,
  TechnologiesSection,
  FAQSection,
} from "../components/CommonSections";

const AnimatedCounter = ({ value }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const stringValue = String(value || "0");
  const match = stringValue.match(/^([\d.]+)/);
  const numericValue = match ? parseFloat(match[1]) : 0;
  const hasDecimal = numericValue % 1 !== 0;
  const extractedSuffix = stringValue.replace(/[\d.]/g, "");
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) setIsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000,
      steps = 60,
      increment = numericValue / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= numericValue) {
        setCount(numericValue);
        clearInterval(timer);
      } else
        setCount(
          hasDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current)
        );
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, numericValue, hasDecimal]);
  return (
    <span ref={ref}>
      {hasDecimal ? count.toFixed(1) : count}
      {extractedSuffix}
    </span>
  );
};

const iconMap = { Headphones, Server, Shield, Settings, Database, Users };

const ITSupportServicesPage = () => {
  const data = IT_SUPPORT_DATA;
  const [activeAccordion, setActiveAccordion] = useState(0);
  const relevantCaseStudies = CASE_STUDIES.filter((cs) =>
    cs.services?.some(
      (s) =>
        s.toLowerCase().includes("support") ||
        s.toLowerCase().includes("maintenance")
    )
  ).slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Split Screen */}
      <section className="relative min-h-[550px] overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[550px] py-20">
            {/* Left Content */}
            <div>
              <Link
                to="/what-we-do"
                className="inline-flex items-center text-gray-400 hover:text-[#b32126] mb-6 transition-colors"
              >
                <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                Back
              </Link>

              <div
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(179,33,38,0.2) 0%, rgba(179,33,38,0.1) 100%)",
                  color: "#f87171",
                }}
              >
                <Headphones className="h-4 w-4 mr-2" />
                Individual Service
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight tracking-tight"
                data-testid="hero-title"
              >
                IT Support
                <span className="block" style={{ color: "#b32126" }}>
                  Services
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-lg">
                {data.hero.subtitle}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  style={{
                    background:
                      "linear-gradient(135deg, #d64449 0%, #b32126 50%, #8f1a1f 100%)",
                  }}
                  data-testid="hero-cta"
                >
                  Get Support
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/resources/case-studies"
                  className="inline-flex items-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all backdrop-blur-sm"
                >
                  View Case Studies
                </Link>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="hidden lg:flex justify-center items-center relative">
              <div className="relative w-full h-[400px]">
                <div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl opacity-30"
                  style={{ background: "#b32126" }}
                />
                <img
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&q=80"
                  alt="IT Support Helpdesk"
                  className="relative z-10 w-full h-full object-cover rounded-2xl shadow-2xl border-4 border-gray-700"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#b32126] text-white p-4 rounded-xl shadow-lg z-20">
                  <div className="text-2xl font-bold">24/7</div>
                  <div className="text-sm">Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {data.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value.includes("/") ? (
                    stat.value
                  ) : (
                    <AnimatedCounter value={stat.value} />
                  )}
                </div>
                <div className="text-red-100 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={data.overview.image}
                alt={data.overview.title}
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {data.overview.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {data.overview.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              IT Support Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive support to keep your business running
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.services.map((service, i) => {
              const Icon = iconMap[service.icon] || Headphones;
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100 hover:border-[#b32126]/20 group"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#b32126] transition-colors">
                    <Icon className="h-7 w-7 text-[#b32126] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((f, idx) => (
                      <li
                        key={idx}
                        className="flex items-center text-sm text-gray-500"
                      >
                        <CheckCircle className="h-4 w-4 text-[#b32126] mr-2 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Support Tiers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Support Plans
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the right level of support for your business
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {data.supportTiers.map((tier, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-8 shadow-lg border-2 ${
                  i === 1 ? "border-[#b32126] relative" : "border-gray-100"
                }`}
              >
                {i === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#b32126] text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {tier.tier}
                </h3>
                <div className="text-[#b32126] font-semibold mb-6">
                  {tier.price}
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-[#b32126] mr-3 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center py-3 px-6 rounded-xl font-semibold transition-colors ${
                    i === 1
                      ? "bg-[#b32126] text-white hover:bg-[#8f1a1f]"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <IndustriesSection
        title="Industries We Serve"
        subtitle="IT support services across diverse industry verticals"
      />

      {relevantCaseStudies.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Support Success Stories
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {relevantCaseStudies.map((study) => (
                <Link
                  key={study.id}
                  to={`/resources/case-studies/${study.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-medium rounded-full">
                        {study.industry}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                      {study.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {study.tagline}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Technologies */}
      <TechnologiesSection />

      {/* Engagement Models */}
      <EngagementModelsSection bgColor="bg-white" />

      {/* FAQ Section */}
      <FAQSection />

      <section className="py-16 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need IT Support?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let&apos;s discuss how we can keep your systems running smoothly.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-xl hover:bg-gray-100 transition-all shadow-lg"
            data-testid="final-cta"
          >
            Contact Us
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ITSupportServicesPage;
